import { z } from 'zod';
import { APP_ENTITIES, ENTITY_ACTIONS, LANGUAGES, USER_ROLES, USER_STATUSES, OFFICES } from '../enums.js';
import { patchSchemaOf } from './common.js';
export const loginSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(1, 'Password is required'),
});
/** Deliberately strict: this app holds a company's commercial relationships. */
export const passwordSchema = z
    .string()
    .min(12, 'Password must be at least 12 characters')
    .max(200)
    .refine((p) => /[a-z]/.test(p), 'Must contain a lowercase letter')
    .refine((p) => /[A-Z]/.test(p), 'Must contain an uppercase letter')
    .refine((p) => /\d/.test(p), 'Must contain a number');
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
});
export const createUserSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    fullName: z.string().trim().min(2).max(120),
    fullNameAr: z.string().trim().max(120).optional(),
    role: z.enum(USER_ROLES),
    office: z.enum(OFFICES).default('beirut'),
    language: z.enum(LANGUAGES).default('en'),
    /**
     * Optional. Left out, the server generates a temporary one and returns it
     * once — an admin adding a colleague should not have to invent a password
     * that meets the policy, and the account is forced to change it anyway.
     */
    password: passwordSchema.optional(),
    /**
     * Which screens they may open.
     *
     * Omitted or null means all of them, which is what every account created
     * before this existed carries. An empty array leaves them the dashboard and
     * nothing else. The UI must send null explicitly to reset somebody to
     * everything — omitting the field leaves it untouched.
     */
    entities: z.array(z.enum(APP_ENTITIES)).nullable().optional(),
    /**
     * Per-screen add/edit/delete, narrowing only.
     *
     * Omitted leaves it untouched; null clears every restriction. A screen left
     * out of the map is unrestricted, which is why an administrator ticking
     * nothing changes nothing.
     */
    entityActions: z.partialRecord(z.enum(APP_ENTITIES), z.array(z.enum(ENTITY_ACTIONS)))
        .nullable().optional(),
});
export const updateUserSchema = patchSchemaOf(createUserSchema.omit({ password: true }))
    .extend({ status: z.enum(USER_STATUSES).optional() });
/** What the client is allowed to know about the signed-in user. */
export const publicUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string(),
    fullName: z.string(),
    fullNameAr: z.string().nullable(),
    role: z.enum(USER_ROLES),
    office: z.enum(OFFICES),
    language: z.enum(LANGUAGES),
    status: z.enum(USER_STATUSES),
    mustChangePassword: z.boolean(),
    /** Null means every screen — see ENTITY_PERMISSIONS in permissions.ts. */
    entities: z.array(z.enum(APP_ENTITIES)).nullable(),
    /**
     * Which of add/edit/delete survive per screen. Null means not narrowed.
     *
     * Sent to the client so buttons can be hidden, which is a convenience only —
     * the server refuses regardless (rule 3).
     */
    entityActions: z.partialRecord(z.enum(APP_ENTITIES), z.array(z.enum(ENTITY_ACTIONS))).nullable(),
});
/**
 * Removing a user.
 *
 * `reassignTo` names who inherits their live work — the companies they own and
 * the deals still open. Optional in the contract so the server can answer with
 * the counts first and let an administrator decide, rather than demanding a
 * name for a user who owns nothing.
 */
export const deleteUserSchema = z.object({
    reassignTo: z.string().uuid().optional(),
});
//# sourceMappingURL=auth.js.map